import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  UserRole,
  type AdminUser,
  type StoreOption,
} from '@asko/shared';
import { AppHeader } from '../components/AppHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EventTimeline } from '../components/EventTimeline';
import { Select, type SelectOption } from '../components/Select';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { formatDateTime, formatPhone } from '../lib/format';

/**
 * Foydalanuvchilar (ADMIN).
 *
 * Sotuvchini yaratish, do'konga biriktirish, o'chirib qo'yish va
 * parolini almashtirish. Alif identifikatori ham shu yerda: u har bir
 * so'rov sarlavhasida ketadi va sotuvchiga bog'langan.
 */

interface Draft {
  name: string;
  phone: string;
  password: string;
  role: UserRole;
  storeId: string;
}

const EMPTY_DRAFT: Draft = {
  name: '',
  phone: '998',
  password: '',
  role: UserRole.SALES,
  storeId: '',
};

export function UsersPage() {
  const { t, apiMessage } = useI18n();
  const { user: me } = useAuth();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [creating, setCreating] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const [storeDraft, setStoreDraft] = useState('');
  const [creatingStore, setCreatingStore] = useState(false);

  const [passwordFor, setPasswordFor] = useState<AdminUser | null>(null);
  /** Kimning amallari ko'rsatilyapti. */
  const [eventsFor, setEventsFor] = useState<AdminUser | null>(null);
  const [disableAsked, setDisableAsked] = useState<AdminUser | null>(null);

  async function reload() {
    setLoading(true);
    try {
      setUsers(await api.get<AdminUser[]>('/admin/users'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    api.get<StoreOption[]>('/admin/stores').then(setStores).catch(() => setStores([]));
  }, []);

  const storeOptions: SelectOption<string>[] = useMemo(
    () => [
      { value: '', label: t('users.noStore') },
      ...stores.map((store) => ({ value: store.id, label: store.name })),
    ],
    [stores, t],
  );

  const roleOptions: SelectOption<UserRole>[] = [
    { value: UserRole.SALES, label: t('role.SALES') },
    { value: UserRole.ADMIN, label: t('role.ADMIN') },
  ];

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await api.post<AdminUser>('/admin/users', {
        name: draft.name.trim(),
        phone: draft.phone,
        password: draft.password,
        role: draft.role,
        storeId: draft.storeId || null,
      });
      setDraft(EMPTY_DRAFT);
      setFormOpen(false);
      setNotice(t('users.created'));
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setCreating(false);
    }
  }

  async function handleCreateStore(event: FormEvent) {
    event.preventDefault();
    setCreatingStore(true);
    setError(null);
    try {
      const store = await api.post<StoreOption>('/admin/stores', { name: storeDraft.trim() });
      setStores((before) => [...before, store].sort((a, b) => a.name.localeCompare(b.name)));
      setStoreDraft('');
      setNotice(t('stores.created'));
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setCreatingStore(false);
    }
  }

  async function patch(user: AdminUser, body: Record<string, unknown>) {
    setError(null);
    try {
      await api.patch<AdminUser>(`/admin/users/${user.id}`, body);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    }
  }

  return (
    <div className="app-shell">
      <AppHeader />

      <main className="app-main app-main--wide">
        <div className="card">
          <div className="history-head">
            <div>
              <h1 className="section-title">{t('users.title')}</h1>
              <p className="section-hint">{t('users.hint')}</p>
            </div>
            <button
              className="btn btn--primary"
              type="button"
              onClick={() => setFormOpen((open) => !open)}
            >
              {formOpen ? t('common.cancel') : t('users.add')}
            </button>
          </div>

          {error && (
            <div className="alert alert--error" role="alert">
              {apiMessage(error)}
            </div>
          )}
          {notice && (
            <div className="alert alert--info" role="status">
              {notice}
            </div>
          )}

          {formOpen && (
            <form className="users-form" onSubmit={handleCreate}>
              <label className="field">
                <span className="field__label">{t('users.name')}</span>
                <input
                  className="field__input"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  required
                />
              </label>

              <label className="field">
                <span className="field__label">{t('users.phone')}</span>
                <input
                  className="field__input field__input--mono"
                  inputMode="numeric"
                  value={formatPhone(draft.phone)}
                  onChange={(e) =>
                    setDraft({ ...draft, phone: e.target.value.replace(/\D/g, '').slice(0, 12) })
                  }
                  required
                />
              </label>

              <label className="field">
                <span className="field__label">{t('users.password')}</span>
                <input
                  className="field__input"
                  type="text"
                  value={draft.password}
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                  placeholder={t('users.passwordHint')}
                  required
                />
              </label>

              <label className="field">
                <span className="field__label">{t('users.role')}</span>
                <Select
                  value={draft.role}
                  options={roleOptions}
                  onChange={(role) => setDraft({ ...draft, role })}
                  ariaLabel={t('users.role')}
                />
              </label>

              <label className="field">
                <span className="field__label">{t('users.store')}</span>
                <Select
                  value={draft.storeId}
                  options={storeOptions}
                  onChange={(storeId) => setDraft({ ...draft, storeId })}
                  ariaLabel={t('users.store')}
                />
              </label>

              <div className="users-form__actions">
                <button className="btn btn--primary" type="submit" disabled={creating}>
                  {creating ? t('users.creating') : t('users.create')}
                </button>
              </div>
            </form>
          )}

          {loading && users.length === 0 ? (
            <div className="center-screen" aria-busy="true">
              <div className="spinner" aria-label={t('common.loading')} />
            </div>
          ) : (
            <div className="history-scroll">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>{t('users.name')}</th>
                    <th>{t('users.phone')}</th>
                    <th>{t('users.role')}</th>
                    <th>{t('users.store')}</th>
                    <th>{t('users.alifId')}</th>
                    <th>{t('users.myidToday')}</th>
                    <th>{t('users.lastLogin')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className={user.isActive ? '' : 'is-off'}>
                      <td>
                        <button
                          className="users-table__name"
                          type="button"
                          onClick={() => setEventsFor(user)}
                          title={t('events.userTitle', { name: user.name })}
                        >
                          {user.name}
                        </button>
                        {!user.isActive && (
                          <span className="history-table__phone">{t('users.disabled')}</span>
                        )}
                      </td>
                      <td className="history-table__date">{formatPhone(user.phone)}</td>
                      <td>{t(`role.${user.role}`)}</td>
                      <td>
                        <Select
                          className="users-table__store"
                          value={user.store?.id ?? ''}
                          options={storeOptions}
                          onChange={(storeId) => void patch(user, { storeId: storeId || null })}
                          ariaLabel={t('users.store')}
                        />
                      </td>
                      <td>
                        {user.alifUserId ? (
                          <span className="alif-id alif-id--ok">
                            {t('users.alifReady', { id: user.alifUserId })}
                          </span>
                        ) : (
                          <span className="alif-id">{t('users.alifSystem')}</span>
                        )}
                      </td>
                      <td>
                        {/* MyID har bir identifikatsiya uchun pul oladi —
                            bu xarajatning ko'rinadigan tarafi. */}
                        <span className="history-table__date">
                          {user.identificationsToday || '—'}
                        </span>
                      </td>
                      <td className="history-table__date">
                        {formatDateTime(user.lastLoginAt) || '—'}
                      </td>
                      <td className="users-table__actions">
                        <button
                          className="btn btn--small"
                          type="button"
                          onClick={() => setPasswordFor(user)}
                        >
                          {t('users.setPassword')}
                        </button>
                        {user.id !== me?.id && (
                          <button
                            className="btn btn--small"
                            type="button"
                            onClick={() =>
                              user.isActive
                                ? setDisableAsked(user)
                                : void patch(user, { isActive: true })
                            }
                          >
                            {user.isActive ? t('users.disable') : t('users.enable')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card">
          <div className="history-head">
            <div>
              <h2 className="section-title">{t('stores.title')}</h2>
              <p className="section-hint">{t('stores.hint')}</p>
            </div>
          </div>

          <form className="stores-form" onSubmit={handleCreateStore}>
            <label className="field">
              <span className="field__label">{t('stores.name')}</span>
              <input
                className="field__input"
                value={storeDraft}
                onChange={(e) => setStoreDraft(e.target.value)}
                placeholder={t('stores.namePlaceholder')}
                required
              />
            </label>
            <button
              className="btn btn--primary"
              type="submit"
              disabled={creatingStore || storeDraft.trim().length < 2}
            >
              {creatingStore ? t('users.creating') : t('stores.add')}
            </button>
          </form>

          <ul className="stores-list">
            {stores.map((store) => (
              <li key={store.id} className="stores-list__item">
                <span>{store.name}</span>
                <span className="stores-list__count">
                  {t('stores.sellers', {
                    n: users.filter((u) => u.store?.id === store.id).length,
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </main>

      {eventsFor && (
        <dialog
          className="modal modal--wide"
          ref={(node) => {
            if (node && !node.open) node.showModal();
          }}
          onCancel={(e) => {
            e.preventDefault();
            setEventsFor(null);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setEventsFor(null);
          }}
        >
          <div className="modal__body">
            <h2 className="modal__title">
              {t('events.userTitle', { name: eventsFor.name })}
            </h2>
            <EventTimeline
              source={`/admin/users/${eventsFor.id}/events`}
              emptyText={t('events.empty')}
            />
            <div className="modal__actions">
              <button className="btn" type="button" onClick={() => setEventsFor(null)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </dialog>
      )}

      {passwordFor && (
        <SetPasswordDialog
          user={passwordFor}
          onClose={() => setPasswordFor(null)}
          onDone={() => {
            setPasswordFor(null);
            setNotice(t('users.passwordSet'));
          }}
        />
      )}

      <ConfirmDialog
        open={disableAsked !== null}
        title={t('users.disableTitle')}
        body={t('users.disableBody', { name: disableAsked?.name ?? '' })}
        confirmLabel={t('users.disable')}
        cancelLabel={t('common.cancel')}
        tone="danger"
        onCancel={() => setDisableAsked(null)}
        onConfirm={() => {
          const target = disableAsked;
          setDisableAsked(null);
          if (target) void patch(target, { isActive: false });
        }}
      />
    </div>
  );
}

/** Ma'mur boshqa foydalanuvchiga parol qo'yadi — joriy parolsiz. */
function SetPasswordDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, apiMessage } = useI18n();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/users/${user.id}/password`, { password });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog
      className="modal"
      ref={(node) => {
        if (node && !node.open) node.showModal();
      }}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form className="modal__body" onSubmit={submit}>
        <h2 className="modal__title">{t('users.setPasswordFor', { name: user.name })}</h2>

        <label className="field">
          <span className="field__label">{t('users.newPassword')}</span>
          <input
            className="field__input"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('users.passwordHint')}
            autoFocus
            required
          />
        </label>

        {error && (
          <div className="alert alert--error" role="alert">
            {apiMessage(error)}
          </div>
        )}

        <div className="modal__actions">
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn--primary" type="submit" disabled={busy || password.length < 8}>
            {t('users.save')}
          </button>
        </div>
      </form>
    </dialog>
  );
}
